class StayAISubscriptions extends HTMLElement {
  constructor() {
    super();
    // Only variants are ever read off the product, so we pass just those (not the full
    // product | json) — on a large enough product, the full payload silently collapsed to just
    // the product handle string instead of the object.
    this.variantsJSON = this.getAttribute("productVariants");
    this.variants = JSON.parse(this.variantsJSON);
    this.initialVariant = this.getAttribute("currentVariantId");
    this.sellingPlanGroups = JSON.parse(this.getAttribute("sellingPlanGroups"));
    this.productFormId = this.getAttribute("productFormId");
    this.frequencyVisible = this.getAttribute("frequencyVisible");
    // Prepaid gift info requirement: when true, requires gift fields when prepaid is selected
    this.prepaidGiftinfoRequired = this.getAttribute("prepaidGiftinfoRequired") === "true";
    // Custom warning message for prepaid gift info validation
    this.prepaidGiftinfoWarningMsg = this.getAttribute("prepaidGiftinfoWarningMsg") || "Please fill in prepaid subscription recipient info.";
    // Prepaid SPG ids (CSV) emitted by Liquid; empty when product has no prepaid plans
    const prepaidIdsCsv = this.getAttribute("prepaidGroupIds") || "";
    this.prepaidGroupIds = new Set(
      prepaidIdsCsv
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .map(String),
    );
  }
  connectedCallback() {
    this.initWidget();
    this.setupEventListeners();
  }
  initWidget() {
    // Radios matched by `value` (stable); labels derived via closest('label') rather than
    // matching `[for='...']` by string, since `id`/`for` are suffixed per-instance in the Liquid
    // (label-for resolves by document-wide id lookup, so duplicate ids let one widget instance
    // activate another's radio — this avoids needing to know that suffix here).
    this.radioOneTime = this.querySelector('input[value="purchaseTypeOneTime"]');
    this.radioSubscription = this.querySelector('input[value="purchaseTypeSubscription"]');
    this.radioSubscriptionPrepaid = this.querySelector('input[value="purchaseTypeSubscriptionPrepaid"]');
    // 3-way state: standard subscription label, the prepaid label, and one-time. Any of these
    // may be absent (one-time only product, or the prepaid one absent when no prepaid SPGs).
    this.purchaseTypeOneTime = this.radioOneTime?.closest("label") || null;
    this.purchaseTypeSubscription = this.radioSubscription?.closest("label") || null;
    this.purchaseTypeSubscriptionPrepaid = this.radioSubscriptionPrepaid?.closest("label") || null;
    // Subscribe-and-save dropdown + wrapper (not prepaid).
    // bubble-buttons, split-button, and side-by-side only have one dropdown, so the || fallbacks
    // target that element when prepaid markup isn't in the DOM.
    // Classes standard-subscription-type / prepaid-subscription-type avoid clashing with
    // widget_layout values like 'standard' and 'side-by-side-buttons'.
    this.sellingPlanSelector =
      this.querySelector(".stayai-subscription-dropdown.standard-subscription-type") ||
      this.querySelector(".stayai-subscription-dropdown");
    this.sellingPlanSelectorPrepaid = this.querySelector(".stayai-subscription-dropdown.prepaid-subscription-type");
    this.subscriptionBox =
      this.querySelector("[data-stayai-subscription-type='standard']") ||
      this.querySelector("[data-retextion-subscription-box]");
    this.subscriptionBoxPrepaid = this.querySelector("[data-stayai-subscription-type='prepaid']");
    this.subscriptionBoxContainer = this.querySelector(".stayai-subscription-frequency-container");
    // Gift form fields (cached always, not just when setting is ON — needed for auto-injection in both cases)
    this.giftCheckbox = this.querySelector('input[name="properties[_gifted-subscription]"]');
    this.giftEmailInput = this.querySelector('input[name="properties[Gift Recipient Email]"]');
    this.giftFirstNameInput = this.querySelector('input[name="properties[Gift Recipient First Name]"]');
    this.giftBox = this.querySelector('div[data-retextion-gift-box]');
    if (this.prepaidGiftinfoRequired) {
      // Note: ATC button is found later in setupFormSubmitValidation() within the form context
      console.log(">>> prepaid gift validation initialized:", {
        giftCheckbox: this.giftCheckbox,
        giftEmailInput: this.giftEmailInput,
        giftFirstNameInput: this.giftFirstNameInput,
        giftBox: this.giftBox,
      });
    }
    const selectedInitialVariant = this.variants.find((variant) => variant.id === Number(this.initialVariant));
    this.currentVariant = selectedInitialVariant || this.variants[0];
    this.updateSellingPlans();
  }
  // Set up event listeners for the purchase type, frequency and variant selectors
  setupEventListeners() {
    // Prefix-match: `name` is suffixed per-instance in the Liquid (radios outside a <form> are
    // grouped document-wide by name, so a shared literal name let one widget's radio uncheck
    // another's). Already scoped to `this`, so the suffix itself doesn't matter here.
    const purchaseTypeRadioButtons = this.querySelectorAll('input[name^="purchaseType"]');
    // Handle purchase type change
    purchaseTypeRadioButtons.forEach((radio) =>
      radio.addEventListener("change", () => this.onPurchaseTypeChange(radio.value)),
    );
    // Handle standard frequency selector change
    if (this.sellingPlanSelector) {
      this.sellingPlanSelector.addEventListener("change", (e) => {
        this.updateSellingPlanInput(e.target.value);
        this.updateProductPrice(e.target.value);
      });
    }
    // Handle prepaid frequency selector change
    if (this.sellingPlanSelectorPrepaid) {
      this.sellingPlanSelectorPrepaid.addEventListener("change", (e) => {
        this.updateSellingPlanInput(e.target.value);
        this.updateProductPrice(e.target.value);
      });
    }
    // Handle variant change from URL
    window.addEventListener("popstate", this.updateVariantID());
    this.observeUrlChanges();
    // Handle browser back/forward navigation.
    // Why this exists: on back-nav from checkout, Liquid re-renders with the server default
    // (e.g. subscription label gets `is-selected`), but the browser's form-state restoration
    // flips `input.checked` back to the user's last choice AFTER our JS init runs. Result:
    // label's `is-selected` class and the underlying radio's `checked` state diverge.
    //
    // Fix: re-sync from radio.checked on every pageshow. We can't use `label.click()` —
    // clicking a label whose radio is already `checked` does not fire a `change` event.
    // Also do not gate on `event.persisted` / navigation.type — Safari does form-state
    // restoration without bfcache, so those flags miss real back-nav cases.
    window.addEventListener("pageshow", () => this.syncStateFromCheckedRadio());
    // Alt 2: Form submit validation for prepaid + gift info
    this.setupFormSubmitValidation();
  }

  setupFormSubmitValidation() {
    // Hide the gift Save button always — we auto-inject fields on ATC now
    const giftSaveButton = this.querySelector('.giftSaveButton');
    if (giftSaveButton) {
      giftSaveButton.style.display = "none";
      console.log(">>> Hidden giftSaveButton (gift fields will be auto-injected on ATC)");
    }

    // Always set up form submit handler for auto-injection (validation only if setting is ON)
    let productForm = document.querySelector(
      'form[action="/cart/add"][data-type="add-to-cart-form"]:not(.installment), ' +
        'form[action="/cart/add"][id*="product-form"]:not(.installment), ' +
        'form[action="/cart/add"][class*="shopify-product-form"]:not(.installment), ' +
        'form[action="/cart/add"][class*="product-form"]:not(.installment), ' +
        'form[action="/cart/add"]:not(.installment)',
    );
    if (this.productFormId !== "") productForm = document.querySelector(this.productFormId) || productForm;
    if (!productForm) {
      console.log(">>> Could not find product form for validation");
      return;
    }
    console.log(">>> Found product form for gift validation:", {
      form: productForm,
      formAction: productForm.action,
      formId: productForm.id,
      formClass: productForm.className,
    });

    // Find submit button WITHIN the form, not document-wide
    this.atcButton = productForm.querySelector('button[type="submit"]');
    console.log(">>> Found ATC button in form:", {
      atcButton: this.atcButton,
      atcButtonTag: this.atcButton?.tagName,
      atcButtonClass: this.atcButton?.className,
      atcButtonText: this.atcButton?.textContent?.trim(),
    });

    // Create validation error message element (hidden by default)
    this.validationMessage = document.createElement("p");
    this.validationMessage.className = "stayai-validation-error";
    this.validationMessage.textContent = this.prepaidGiftinfoWarningMsg;
    this.validationMessage.style.display = "none";
    if (this.atcButton) {
      this.atcButton.parentNode.insertBefore(this.validationMessage, this.atcButton.nextSibling);
      console.log(">>> Created validation message element:", {
        message: this.validationMessage,
        text: this.validationMessage.textContent,
      });
    }

    // Force gift checkbox to stay checked when prepaid is active (only when validation is ON)
    if (this.prepaidGiftinfoRequired && this.giftCheckbox) {
      this.giftCheckbox.addEventListener("change", (e) => {
        if (this.radioSubscriptionPrepaid?.checked && !e.target.checked) {
          // Prepaid is selected but user unchecked gift → force it back to checked
          e.target.checked = true;
        }
      });
    }

    // Listen to gift field changes to update button state (only when validation is ON)
    if (this.prepaidGiftinfoRequired) {
      if (this.giftEmailInput) {
        this.giftEmailInput.addEventListener("input", () => this.updateATCButtonState());
      }
      if (this.giftFirstNameInput) {
        this.giftFirstNameInput.addEventListener("input", () => this.updateATCButtonState());
      }
    }

    // Form submit handler: validate (if setting ON) and auto-inject gift fields (always when prepaid selected)
    productForm.addEventListener("submit", (e) => {
      const isPrepaidChecked = this.radioSubscriptionPrepaid?.checked;
      if (!isPrepaidChecked) return; // Only process if prepaid is selected

      const emailValid = this.giftEmailInput?.value?.trim();
      const firstNameValid = this.giftFirstNameInput?.value?.trim();

      // Validation: only block if setting is ON and fields are empty
      if (this.prepaidGiftinfoRequired && (!emailValid || !firstNameValid)) {
        e.preventDefault();
        console.log(">>> Gift info validation failed: email or first name missing");
        this.showGiftValidationError();
        return false;
      }

      // Auto-inject gift fields into form payload (always when prepaid selected, regardless of setting)
      console.log(">>> Auto-injecting gift fields into form payload");
      this.injectGiftFieldsIntoForm(productForm);
    });
  }

  injectGiftFieldsIntoForm(form) {
    if (!form) return;

    // Clear any old dynamic gift fields
    form.querySelectorAll('.dynamic-gift-field').forEach((el) => el.remove());
    console.log(">>> Cleared old dynamic gift fields");

    // Helper to create hidden input
    const createHiddenInput = (name, value) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      input.className = "dynamic-gift-field";
      return input;
    };

    // Always add the _gifted-subscription flag
    const giftedInput = createHiddenInput("properties[_gifted-subscription]", "true");
    form.appendChild(giftedInput);

    // Add gift recipient info
    const emailValue = this.giftEmailInput?.value?.trim();
    const firstNameValue = this.giftFirstNameInput?.value?.trim();
    const lastNameValue = this.querySelector('input[name="properties[Gift Recipient Last Name]"]')?.value?.trim();
    const messageValue = this.querySelector('textarea[name="properties[Gift Message]"]')?.value?.trim();

    if (emailValue) {
      form.appendChild(createHiddenInput("properties[Gift Recipient Email]", emailValue));
    }
    if (firstNameValue) {
      form.appendChild(createHiddenInput("properties[Gift Recipient First Name]", firstNameValue));
    }
    if (lastNameValue) {
      form.appendChild(createHiddenInput("properties[Gift Recipient Last Name]", lastNameValue));
    }
    if (messageValue) {
      form.appendChild(createHiddenInput("properties[Gift Message]", messageValue));
    }

    console.log(">>> Gift fields injected into form:", {
      gifted: true,
      email: emailValue ? "✓" : "✗",
      firstName: firstNameValue ? "✓" : "✗",
      lastName: lastNameValue ? "✓" : "✗",
      message: messageValue ? "✓" : "✗",
    });
  }

  showGiftValidationError() {
    // Scroll to gift form so user sees the validation message and fields
    console.log(">>> Gift info validation blocked — scrolling to gift form");
    if (this.giftBox) {
      this.giftBox.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }

  // Force is-selected classes + frequency-box visibility + hidden selling_plan input
  // to match whichever radio currently has `checked`. Idempotent on initial load.
  syncStateFromCheckedRadio() {
    const checkedRadio = this.querySelector('input[name^="purchaseType"]:checked');
    if (checkedRadio) {
      this.onPurchaseTypeChange(checkedRadio.value);
    } else if (this.radioOneTime) {
      // Defensive fallback: no radio checked at all → reset to one-time
      this.radioOneTime.checked = true;
      this.onPurchaseTypeChange("purchaseTypeOneTime");
    }
    this.updateSellingPlans();
  }
  // Central handler for radio change. Drives show/hide for all three states.
  onPurchaseTypeChange(value) {
    if (value === "purchaseTypeSubscription") {
      this.showSubscriptionBox("standard");
      this.updateProductPrice(this.sellingPlanSelector ? this.sellingPlanSelector.value : "");
      this.updateGiftFormRequirement(false);
    } else if (value === "purchaseTypeSubscriptionPrepaid") {
      this.showSubscriptionBox("prepaid");
      this.updateProductPrice(this.sellingPlanSelectorPrepaid ? this.sellingPlanSelectorPrepaid.value : "");
      this.updateGiftFormRequirement(true);
    } else {
      this.hideSubscriptionBox();
      this.updateProductPrice("");
      this.updateGiftFormRequirement(false);
    }
  }
  // Alt 1: HTML5 native validation + Alt 2: force-check & show gift form when prepaid active
  updateGiftFormRequirement(isPrepaidActive) {
    if (!this.prepaidGiftinfoRequired) return;
    if (!this.giftEmailInput || !this.giftFirstNameInput) return;

    console.log(">>> updateGiftFormRequirement:", {
      isPrepaidActive,
      atcButton: this.atcButton,
    });

    if (isPrepaidActive) {
      // Force-check the gift checkbox (Alt 2)
      if (this.giftCheckbox) this.giftCheckbox.checked = true;
      // Show the gift box
      if (this.giftBox) this.giftBox.classList.add("is-visible");
      // Make email & name required (Alt 1)
      this.giftEmailInput.required = true;
      this.giftFirstNameInput.required = true;
      // Disable ATC button initially if fields are empty
      console.log(">>> prepaid activated — disabling ATC button");
      this.updateATCButtonState();
    } else {
      // Uncheck gift checkbox when prepaid deselected
      if (this.giftCheckbox) this.giftCheckbox.checked = false;
      // Hide the gift box
      if (this.giftBox) this.giftBox.classList.remove("is-visible");
      // Remove required from email & name
      this.giftEmailInput.required = false;
      this.giftFirstNameInput.required = false;
      // Re-enable ATC button
      if (this.atcButton) {
        this.atcButton.disabled = false;
        console.log(">>> prepaid deactivated — enabling ATC button", {
          atcButton: this.atcButton,
          disabled: this.atcButton.disabled,
        });
      }
    }
  }

  updateATCButtonState() {
    if (!this.atcButton || !this.prepaidGiftinfoRequired) return;
    const isPrepaidChecked = this.radioSubscriptionPrepaid?.checked;
    if (!isPrepaidChecked) return; // Only enforce when prepaid is checked

    const emailValid = this.giftEmailInput?.value?.trim();
    const firstNameValid = this.giftFirstNameInput?.value?.trim();
    const areFieldsFilled = emailValid && firstNameValid;

    this.atcButton.disabled = !areFieldsFilled;

    // Show/hide validation error message
    if (this.validationMessage) {
      this.validationMessage.style.display = areFieldsFilled ? "none" : "block";
    }

    console.log(">>> updateATCButtonState:", {
      atcButton: this.atcButton,
      atcButtonTag: this.atcButton?.tagName,
      atcButtonClass: this.atcButton?.className,
      atcButtonDisabled: this.atcButton?.disabled,
      emailValue: emailValid ? `"${emailValid}"` : "(empty)",
      firstNameValue: firstNameValid ? `"${firstNameValid}"` : "(empty)",
      areFieldsFilled,
      messageVisible: !areFieldsFilled,
    });
  }
  // Get variant ID from the URL and update the variant
  updateVariantID = () => {
    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.has("variant")) return;
    const variantId = Number(urlParams.get("variant"));
    if (variantId && variantId !== this.currentVariant.id) {
      this.currentVariant = this.variants.find((v) => v.id === variantId);
      this.updateSellingPlans();
    }
  };
  // Monitor URL changes without overriding history methods
  observeUrlChanges = () => {
    let lastUrl = window.location.href;
    new MutationObserver(() => {
      if (window.location.href !== lastUrl) {
        lastUrl = window.location.href;
        this.updateVariantID();
      }
    }).observe(document, { subtree: true, childList: true });
  };
  // Return true if the given group id was flagged as prepaid by Liquid.
  isPrepaidGroup(groupId) {
    return this.prepaidGroupIds.has(String(groupId));
  }
  // Excludes box-builder/BYOB groups from the frequency dropdowns. Brittle name-based heuristic
  // (Shopify has no field flagging a bundle group) — checks both group.name and options[0].name.
  isBundleGroup(group) {
    const haystack = `${group?.name || ""} ${group?.options?.[0]?.name || ""}`.toLowerCase();
    return haystack.includes("bundle") || haystack.includes("box");
  }
  // Populate the subscription frequency selector(s) with selling plans for the current variant
  updateSellingPlans() {
    const stayWidget = this.querySelector(`.stayai-subscriptions`);
    if (!stayWidget) return;
    // Bundle/box-builder groups are filtered out entirely. 'gift'/'prepaid'/'prepay' are
    // partition signals, not exclusion criteria — Liquid put those SPG ids into prepaidGroupIds
    // for us.
    const groupMap = new Map(
      this.sellingPlanGroups.filter((g) => !this.isBundleGroup(g)).map((g) => [String(g.id), g]),
    );
    // Partition allocations into standard vs prepaid based on prepaidGroupIds set
    const standardPlans = [];
    const prepaidPlans = [];
    this.currentVariant.selling_plan_allocations.forEach((spa) => {
      const group = groupMap.get(String(spa.selling_plan_group_id));
      const plan = group?.selling_plans.find((sp) => sp.id === spa.selling_plan_id);
      if (!plan) return;
      if (this.isPrepaidGroup(spa.selling_plan_group_id)) {
        prepaidPlans.push(plan);
      } else {
        standardPlans.push(plan);
      }
    });
    const totalPlans = standardPlans.length + prepaidPlans.length;
    if (totalPlans === 0) {
      this.updateSellingPlanInput("");
      stayWidget.classList.toggle("is-visible", false);
      return;
    }
    stayWidget.classList.toggle("is-visible", true);
    // Populate the standard dropdown (this may be the only dropdown in non-`standard` widget layouts)
    if (this.sellingPlanSelector) {
      this.sellingPlanSelector.innerHTML = standardPlans
        .map((plan) => `<option value="${plan.id}">${plan.options[0].value}</option>`)
        .join("");
    }
    // Populate the prepaid dropdown when present
    if (this.sellingPlanSelectorPrepaid) {
      this.sellingPlanSelectorPrepaid.innerHTML = prepaidPlans
        .map((plan) => `<option value="${plan.id}">${plan.options[0].value}</option>`)
        .join("");
    }
    // No blank placeholder needed — a <select> with no option marked `selected` defaults its
    // `.value` to the first one.
    const selectedType = this.getSelectedSubscriptionType();
    if (selectedType === "standard") {
      this.updateSellingPlanInput(this.sellingPlanSelector?.value || "");
    } else if (selectedType === "prepaid") {
      this.updateSellingPlanInput(this.sellingPlanSelectorPrepaid?.value || "");
    } else {
      this.updateSellingPlanInput("");
    }
    // Show or hide the subscription frequency selector(s) based on the current selection
    this.subscriptionBox?.classList.toggle("is-visible", selectedType === "standard");
    this.subscriptionBoxPrepaid?.classList.toggle("is-visible", selectedType === "prepaid");
    if (this.subscriptionBoxContainer)
      this.subscriptionBoxContainer.classList.toggle("subscriptionBoxContainer", selectedType !== null);
    // Pre-paint the prepaid price label even when prepaid radio isn't active, so the
    // strikethrough ($variant.price × shipmentCount) shows on initial paint. updateProductPrice
    // routes writes by allocation.group → this only touches prepaid labels, doesn't clobber
    // the standard/one-time labels updated by the active-plan call below.
    if (this.sellingPlanSelectorPrepaid?.value) {
      this.updateProductPrice(this.sellingPlanSelectorPrepaid.value);
    }
    // Update prices for the currently-active plan
    if (selectedType === "standard") {
      this.updateProductPrice(this.sellingPlanSelector?.value || standardPlans[0]?.id || "");
    } else if (selectedType === "prepaid") {
      this.updateProductPrice(this.sellingPlanSelectorPrepaid?.value || prepaidPlans[0]?.id || "");
    } else {
      this.updateProductPrice("");
    }
  }
  // Returns 'standard' | 'prepaid' | null based on which radio currently has is-selected on its label
  getSelectedSubscriptionType() {
    if (this.purchaseTypeSubscriptionPrepaid?.classList.contains("is-selected")) return "prepaid";
    if (this.purchaseTypeSubscription?.classList.contains("is-selected")) return "standard";
    return null;
  }
  // Show the frequency selector for the given subscription type ('standard' | 'prepaid')
  showSubscriptionBox(subscriptionType = "standard") {
    const isPrepaid = subscriptionType === "prepaid";
    const activeBox = isPrepaid ? this.subscriptionBoxPrepaid : this.subscriptionBox;
    const inactiveBox = isPrepaid ? this.subscriptionBox : this.subscriptionBoxPrepaid;
    const activeSelector = isPrepaid ? this.sellingPlanSelectorPrepaid : this.sellingPlanSelector;
    activeBox?.classList.toggle("stayai-disable", false);
    activeBox?.classList.toggle("is-visible", true);
    inactiveBox?.classList.toggle("is-visible", false);
    if (this.subscriptionBoxContainer) this.subscriptionBoxContainer.classList.toggle("subscriptionBoxContainer", true);
    this.purchaseTypeOneTime?.classList.toggle("is-selected", false);
    if (isPrepaid) {
      this.purchaseTypeSubscription?.classList.toggle("is-selected", false);
      this.purchaseTypeSubscriptionPrepaid?.classList.toggle("is-selected", true);
    } else {
      this.purchaseTypeSubscriptionPrepaid?.classList.toggle("is-selected", false);
      this.purchaseTypeSubscription?.classList.toggle("is-selected", true);
    }
    this.updateSellingPlanInput(activeSelector?.value || "");
  }
  // Hide both frequency selectors (one-time selected)
  hideSubscriptionBox() {
    if (this.subscriptionBoxContainer) this.subscriptionBoxContainer.classList.toggle("is-visible", false);
    this.subscriptionBox?.classList.toggle("is-visible", false);
    this.subscriptionBox?.classList.toggle("disable", true);
    this.subscriptionBoxPrepaid?.classList.toggle("is-visible", false);
    this.subscriptionBoxPrepaid?.classList.toggle("disable", true);
    this.purchaseTypeSubscription?.classList.toggle("is-selected", false);
    this.purchaseTypeSubscriptionPrepaid?.classList.toggle("is-selected", false);
    this.purchaseTypeOneTime?.classList.toggle("is-selected", true);
    this.updateSellingPlanInput("");
  }
  // Check if any subscription option (standard or prepaid) is selected
  isSubscriptionOptionSelected() {
    return this.getSelectedSubscriptionType() !== null;
  }
  //Add selling plan input to form with specified selling plan ID
  updateSellingPlanInput(sellingPlanId) {
    let productForm = document.querySelector(
      'form[action="/cart/add"][data-type="add-to-cart-form"]:not(.installment), ' +
        'form[action="/cart/add"][id*="product-form"]:not(.installment), ' +
        'form[action="/cart/add"][class*="shopify-product-form"]:not(.installment), ' +
        'form[action="/cart/add"][class*="product-form"]:not(.installment), ' +
        'form[action="/cart/add"]:not(.installment)',
    );
    if (this.productFormId !== "") productForm = document.querySelector(this.productFormId) || productForm;
    if (!productForm) return;

    // Find or create the hidden input for selling plan
    let sellingPlanInput = productForm.querySelector("input[name='selling_plan']");
    if (!sellingPlanInput) {
      sellingPlanInput = document.createElement("input");
      sellingPlanInput.type = "hidden";
      sellingPlanInput.name = "selling_plan";
      sellingPlanInput.value = sellingPlanId;
      productForm.appendChild(sellingPlanInput);
    } else {
      sellingPlanInput.value = sellingPlanId;
    }
    // Dispatch event with the selling plan value
    this.fireStayAiEvent("stay-ai-subscription-changed", sellingPlanId);
  }
  updateProductPrice(sellingPlanId) {
    const selectedSellingPlanAllocation = this.currentVariant.selling_plan_allocations.find(
      (spa) => spa.selling_plan_id == sellingPlanId,
    );
    const sellingPlan = this.sellingPlanGroups
      .find((group) => group.selling_plans.some((sp) => sp.id == sellingPlanId))
      ?.selling_plans.find((sp) => sp.id == sellingPlanId);
    const oneTimeLblPrice = this.querySelector("[data-stayai-onetime-price]");
    const subscriptionLblPrice = this.querySelector("[data-stayai-subscription-price]");
    const prepaidLblPrice = this.querySelector("[data-stayai-prepaid-price]");
    const savingLbl = this.querySelector(".subscription-savings:not(.prepaid-savings)");
    const prepaidSavingLbl = this.querySelector(".prepaid-savings");
    const onetimePrice = this.formatMoney(this.currentVariant.price);
    const compareAtPrice = this.formatMoney(this.currentVariant.compare_at_price);
    let subscriptionPrice = onetimePrice;

    // Update one-time price
    if (oneTimeLblPrice && oneTimeLblPrice.textContent !== onetimePrice) {
      oneTimeLblPrice.innerHTML =
        compareAtPrice !== "$0.00" && compareAtPrice !== onetimePrice
          ? `<s>${compareAtPrice}</s> ${onetimePrice}`
          : onetimePrice;
    }

    // Update subscription/prepaid price for the active plan
    if (selectedSellingPlanAllocation) {
      subscriptionPrice = this.formatMoney(selectedSellingPlanAllocation.price);
      const isPrepaidActive = this.isPrepaidGroup(selectedSellingPlanAllocation.selling_plan_group_id);
      const targetPriceLbl = isPrepaidActive ? prepaidLblPrice : subscriptionLblPrice;
      const targetSavingLbl = isPrepaidActive ? prepaidSavingLbl : savingLbl;
      // Strikethrough price: for prepaid, allocation.price is the total across N
      // shipments, so the strikethrough must also span N shipments to be apples-to-apples.
      // Parse N from the plan label, e.g. "Prepaid Subscription - Ships every month, 3 total shipments".
      let strikethroughPrice = onetimePrice;
      if (isPrepaidActive) {
        const planLabel = sellingPlan?.options?.[0]?.value || sellingPlan?.name || "";
        const match = planLabel.match(/(\d+)\s*total\s*shipments?/i);
        const shipmentCount = match ? parseInt(match[1], 10) : 1;
        strikethroughPrice = this.formatMoney(this.currentVariant.price * shipmentCount);
      }
      // Compare against innerHTML, not textContent — on initial paint the Liquid-rendered
      // textContent may equal `subscriptionPrice` (e.g. prepaid label has "$38.22" already)
      // while the desired innerHTML still differs because we're adding a strikethrough.
      if (targetPriceLbl) {
        const desiredHtml =
          subscriptionPrice !== strikethroughPrice
            ? `<s class="fullPrice">${strikethroughPrice}</s>${subscriptionPrice}`
            : subscriptionPrice;
        if (targetPriceLbl.innerHTML !== desiredHtml) targetPriceLbl.innerHTML = desiredHtml;
      }
      if (isPrepaidActive) {
        // Prepaid path — allocation.price is the bundle total (e.g. 3 shipments × unit price),
        // so `variant.price - allocation.price` is negative and the standard gate below would
        // clear the savings label. Read the discount directly from the plan's price_adjustments
        // instead. `value` here is per-shipment percent off (or per-shipment money amount).
        const adj = sellingPlan?.price_adjustments?.[0];
        if (adj && targetSavingLbl && adj.value > 0) {
          const text = adj.value_type === "percentage" ? `${adj.value}%` : this.formatMoney(adj.value);
          if (targetSavingLbl.textContent !== text) targetSavingLbl.textContent = text;
        } else if (targetSavingLbl) {
          targetSavingLbl.textContent = "";
        }
      } else {
        // Standard subscribe-and-save path — discount = unit price diff.
        let discount = this.currentVariant.price - selectedSellingPlanAllocation.price;
        if (discount > 0 && targetSavingLbl) {
          if (sellingPlan?.price_adjustments[0].value_type === "percentage") {
            discount = `${sellingPlan.price_adjustments[0].value}%`;
          } else {
            discount = this.formatMoney(discount);
          }
          if (targetSavingLbl.textContent !== discount) targetSavingLbl.textContent = discount;
        } else if (targetSavingLbl) {
          targetSavingLbl.textContent = "";
        }
      }
    }
  }
  // Toggle checked state based on the currently checked radio button
  toggleRadioButtons() {
    this.radioOneTime.checked = !this.radioOneTime.checked;
    this.radioSubscription.checked = !this.radioSubscription.checked;
  }
  // Format money based on Shopify's active currency
  formatMoney(money) {
    const { Shopify, theme } = window;
    const currency = Shopify?.currency?.active || "USD";
    const locale = Shopify?.locale || "en";
    const hasShopifyFormatMoney = Shopify && typeof Shopify?.formatMoney === "function";
    const hasThemeUtilsFormatMoney = theme?.utils && typeof theme?.utils?.formatMoney === "function";
    const formattedMoney = (money / 100).toFixed(2);
    let formatted;
    const currencyFormatter = new Intl.NumberFormat(locale, {
      currency,
      style: "currency",
    });
    if (!hasShopifyFormatMoney && !hasThemeUtilsFormatMoney) {
      return currencyFormatter.format(Number(formattedMoney));
    }
    formatted = hasShopifyFormatMoney ? Shopify.formatMoney(money) : theme.utils.formatMoney(money);
    return formatted || currencyFormatter.format(Number(formattedMoney));
  }
  fireStayAiEvent(eventName, sellingPlanId) {
    // widgetId lets listeners on a page with multiple widget instances filter to their own —
    // the event stays on `window` (other integrations may rely on that), but every instance
    // now receives every instance's events.
    const event = new CustomEvent(eventName, {
      detail: {
        sellingPlanId: sellingPlanId,
        widgetId: this.id,
      },
    });
    window.dispatchEvent(event);
  }
}
// Define the custom element
customElements.define("purchase-options", StayAISubscriptions);
